// The board view as Chessground wants it: the configuration to `set` and the
// arrows to draw. Plain functions over a `BoardView`, so the translation is
// tested without a DOM; index.tsx only hands their results to Chessground.
import type { Config } from '@lichess-org/chessground/config';
import type { DrawShape } from '@lichess-org/chessground/draw';
import type { Key } from '@lichess-org/chessground/types';
import type { BoardBrush, BoardView } from '@chess/api-review/board';

/**
 * Each meaning's Chessground brush, named by colour (index.tsx themes them):
 * green the move to play, whether the engine's, the book's or a line's; red
 * a played blunder; grey any other played move; blue a hint.
 */
export const BRUSH_KEYS: Readonly<Record<BoardBrush, string>> = {
  best: 'green',
  book: 'green',
  variation: 'green',
  played: 'grey',
  blunder: 'red',
  hint: 'blue',
};

export interface GroundOptions {
  /** Light the king in check. Off: the board has not shown it so far. */
  showCheck?: boolean;
}

/**
 * The Chessground configuration for a view: the position, the side at the
 * bottom and to move, the last move, and the moves it may offer, as a fresh
 * Map each time (Chessground keeps the one it is given). `lastMove` and
 * `check` are always set, so a view without them clears the old ones.
 */
export function groundConfig(view: BoardView, options: GroundOptions = {}): Config {
  const dests = new Map<Key, Key[]>();
  for (const [from, to] of view.dests) dests.set(from as Key, [...to] as Key[]);
  return {
    fen: view.fen,
    orientation: view.orientation,
    turnColor: view.turn,
    lastMove: view.lastMove ? [view.lastMove.from as Key, view.lastMove.to as Key] : undefined,
    check: options.showCheck && view.check ? view.turn : false,
    movable: { color: view.movable, dests },
  };
}

/** The view's arrows as Chessground shapes, each meaning drawn with its brush. */
export function shapesOf(view: BoardView, brushKeys: Readonly<Record<BoardBrush, string>> = BRUSH_KEYS): DrawShape[] {
  return view.arrows.map((a) => ({ orig: a.from as Key, dest: a.to as Key, brush: brushKeys[a.brush] }));
}
